import React, { useMemo } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { JsonLd } from "react-schemaorg";
import { Organization, WithContext } from "schema-dts";

import {
  BrandDetail,
  ProductCategory,
  ProductsByCategory,
} from "custom-types/BrandDetail";
import useDomain from "hooks/useDomain";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { generateOrganizationSchema } from "utils/generateOrganizationSchema";
import formatImgixUrl from "utils/image/formatImgixUrl";

import { CountryCodes } from "pages/brands/[slug]";

import { SailthruMetaTags } from "../SailthruMetaTags";

enum CountryAltAvailability {
  "United States" = "showUsAlt",
  "Canada" = "showCaAlt",
}

const titleCase = (s: string) => {
  const nonCapitalizedWords = ["and", "or"];
  const words = s.split(" ");
  return words
    .map((word) => {
      if (nonCapitalizedWords.includes(word.toLowerCase())) {
        return word.toLowerCase();
      }
      return word.replace(/./, (match) => match.toUpperCase());
    })
    .join(" ");
};

export const BrandHead: React.FC<{
  brand: BrandDetail;
  categoryName?: string;
  page?: number;
  referringPage?: string;
  children?: JSX.Element[] | JSX.Element | string;
  sortedCategories?: ProductCategory[];
  productsByCategory?: ProductsByCategory;
}> = ({
  brand,
  categoryName,
  page,
  referringPage,
  sortedCategories,
  productsByCategory,
}) => {
  const router = useRouter();
  const domain = useDomain();
  const domainCountryCode = useDomainCountryCode();
  const shopPage = referringPage === "brand-shop";

  const currentPath = `${domain}${router.asPath}`;
  const { description, logo, name, tagline } = brand;
  const title = shopPage
    ? `Shop ${brand.name}`
    : categoryName
      ? `${name} ${titleCase(categoryName)} on Leafly${
          Number(page) > 1 ? "| Page " + page : ""
        }`
      : `${[name, tagline].filter((s) => !!s).join(": ")} | Leafly`;

  const generalDescription = shopPage
    ? `Browse ${brand.name} products near you. Order weed for pick up or same-day delivery, find reviews & menus from the top dispensaries.`
    : categoryName
      ? `Find your favorite ${name} ${categoryName} while discovering what's new. See what's available near you for pickup or delivery!${
          Number(page) > 1 ? " Page " + page : ""
        }`
      : `Learn about ${name} including who they are, their products, and where you can find them.`;
  const shortDescription =
    (description ?? "").length > 200
      ? `${description?.slice(0, 199)}…`
      : description;
  const logoUrl = formatImgixUrl(logo);
  const tags = [
    name,
    "brand-page",
    ...brand.availableStates,
    ...brand.availableCountries,
  ];

  const { showCaAlt, showUsAlt } = useMemo(() => {
    const initialState = { showCaAlt: false, showUsAlt: false };

    return brand.productCategories.reduce((acc, curr) => {
      const countryAltKey = CountryAltAvailability[curr.country];
      if (acc[countryAltKey]) {
        return acc;
      }

      const shouldShowAlt = categoryName
        ? curr.country !== CountryCodes[domainCountryCode] &&
          curr.seoName === categoryName
        : curr.country !== CountryCodes[domainCountryCode];

      return {
        ...acc,
        [countryAltKey]: shouldShowAlt,
      };
    }, initialState);
  }, [brand, categoryName]);

  const canonicalUrl = currentPath.split("?")[0];

  return (
    <>
      <Head>
        <title>{title}</title>

        {!shopPage && (
          <link
            rel="canonical"
            href={
              // TODO: add pagination here once we have it
              canonicalUrl
            }
          />
        )}
        <meta name="description" content={generalDescription} />

        {logoUrl && (
          <meta property="og:image" content={`${logoUrl}?w=1200&fm=jpg`} />
        )}
        <meta
          property="og:description"
          content={shortDescription || generalDescription}
        />
        <meta property="og:title" content={title} />
        <meta property="og:type" content="webpage" />
        {showCaAlt && (
          <link
            rel="alternate"
            href={`https://www.leafly.ca${router.asPath}`}
            hrefLang="en-ca"
          />
        )}
        {showUsAlt && (
          <link
            rel="alternate"
            href={`https://www.leafly.com${router.asPath}`}
            hrefLang="en-us"
          />
        )}

        {shopPage && <meta name="robots" content="noindex" />}
      </Head>
      <JsonLd<Organization>
        item={
          generateOrganizationSchema(
            brand,
            sortedCategories,
            productsByCategory,
            domainCountryCode,
          ) as WithContext<Organization>
        }
      />
      <SailthruMetaTags
        description={shortDescription || generalDescription}
        imageUrl={logoUrl}
        tags={tags}
        title={title}
      />
    </>
  );
};
